import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { getThemeConfig } from "./themeConfig";
import SectionTitle from "./fields/SectionTitle";
import ColorField from "./fields/ColorField";
import WallpaperField from "./fields/WallpaperField";
import BackgroundField from "./fields/BackgroundField";
import ColorPairField from "./fields/ColorPairField";
import RoundingField from "./fields/RoundingField";
import MatchGalleryToggles from "./fields/MatchGalleryToggles";

// DELIBERATELY NOT IMPORTED HERE: ./paletteExtract.
//
// paletteExtract imports `colorthief`, which is NOT a declared dependency of
// this repo. Nothing referenced <ThemeEditor> until now, so webpack never had
// to resolve it; the moment LinkVault started rendering this editor (#2497) the
// missing package became a hard `Module not found` at build time and the whole
// change had to be reverted (#2500). A lazy `import()` does NOT help — webpack
// resolves lazy specifiers at build time too.
//
// So image-palette extraction is injected by the caller via `onImageExtract`
// instead. The token surfaces that actually want it compose
// <ThemeEditorWithPalette> (which owns the paletteExtract import); LinkVault's
// legacy path passes nothing and keeps ColorThief out of its module graph.
// Pinned by ThemeEditor.noPaletteDep.test.js.

// Controlled, resourceType-parameterized theme editor. Replaces VaultDesign's
// internal per-surface branching with a declarative config read.
// props: resourceType, value (theme object), onChange(nextTheme)
// `header` renders as the FIRST child inside the editor's root Box, so a
// surface can prepend its own flag-gated extras (e.g. LinkVault's Palette
// Library) without changing the surrounding markup.
const ThemeEditor = ({
  resourceType = "linkvault",
  value,
  onChange,
  header = null,
  onImageExtract = null,
  // gordon-0917-themes-popup-add-business-box-and-linkvault: the ThemesPopup's
  // Business Box / LinkVault sections render their OWN single "Match the
  // Gallery" toggle (Theming/MatchGalleryToggle) above this editor instead of
  // the per-token <MatchGalleryToggles> below -- both control the same
  // `match_gallery` field, so showing both would be a confusing duplicate.
  // Default false keeps every existing call site (InnerForm's Business Box
  // "Theme" tab, LinkVault's "Vault Design" tab) byte-identical.
  hideMatchGallery = false,
}) => {
  const config = getThemeConfig(resourceType);
  const theme = value || {};

  // Legacy (LinkVault) path — {bg_color, wallpaper, accent, preset}.
  if (config.shape === "legacy") {
    const setField = (key, val) => onChange({ ...theme, [key]: val });
    const isNeutral =
      !theme.bg_color && !theme.wallpaper && !theme.accent && !theme.preset;

    return (
      <Box sx={{ pt: 1, pb: 2, px: 1 }} data-testid="theme-editor-linkvault">
        {header}
        <Typography
          sx={{ color: "#565556", fontSize: "14px", fontFamily: "DM Sans", mb: 2 }}
        >
          Customize how your LinkVault looks. Leave everything unset for the
          default look.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Background color</SectionTitle>
          <ColorField
            value={theme.bg_color}
            onChange={(v) => setField("bg_color", v)}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Wallpaper</SectionTitle>
          <WallpaperField
            value={theme.wallpaper}
            onChange={(v) => setField("wallpaper", v)}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <SectionTitle>Accent color</SectionTitle>
          <ColorField
            value={theme.accent}
            onChange={(v) => setField("accent", v)}
          />
        </Box>

        {!isNeutral && (
          <Button
            data-testid="theme-reset"
            onClick={() => onChange({})}
            sx={{
              color: "#495057",
              textTransform: "capitalize",
              fontSize: "14px",
              fontWeight: 500,
              mt: 1,
            }}
          >
            Reset to default
          </Button>
        )}

        {config.matchGallery && !hideMatchGallery && (
          <MatchGalleryToggles
            value={theme.match_gallery}
            tokens={config.matchTokens}
            onChange={(v) => onChange({ ...theme, match_gallery: v })}
          />
        )}
      </Box>
    );
  }

  // Token path (gallery / business_box).
  const t = theme || {};
  const setTok = (key, val) => onChange({ ...t, [key]: val });

  // Injected by <ThemeEditorWithPalette>; absent here the upload still sets the
  // background image, it just does not pre-fill a derived palette.
  const handleImageExtract = onImageExtract
    ? (url) => onImageExtract(url, t)
    : undefined;

  return (
    <Box sx={{ pt: 1, pb: 2, px: 1 }} data-testid={`theme-editor-${resourceType}`}>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Background</SectionTitle>
        <BackgroundField
          value={t.background}
          onChange={(v) => setTok("background", v)}
          onImageExtract={handleImageExtract}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Surface / Panel</SectionTitle>
        <ColorPairField
          testId="pair-surface"
          label="Surface"
          value={t.surface}
          onChange={(v) => setTok("surface", v)}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Buttons</SectionTitle>
        <ColorPairField
          testId="pair-button"
          label="Button"
          value={t.button}
          onChange={(v) => setTok("button", v)}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Cards</SectionTitle>
        <ColorPairField
          testId="pair-card"
          label="Card"
          value={t.card}
          onChange={(v) => setTok("card", v)}
        />
      </Box>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Corner rounding</SectionTitle>
        <RoundingField value={t.corner_rounding} onChange={(v) => setTok("corner_rounding", v)} />
      </Box>
      {config.matchGallery && !hideMatchGallery && (
        <MatchGalleryToggles
          value={t.match_gallery}
          tokens={config.matchTokens}
          onChange={(v) => setTok("match_gallery", v)}
        />
      )}
    </Box>
  );
};

export default ThemeEditor;
